import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Search, Link2, Unlink, Check, Loader2, Copy } from 'lucide-react';

/**
 * Привязка записи к человеку из гипнозио. Только для администратора.
 *
 * Право «слышать свою проработку» держится на id профиля в гипнозио, а не на
 * имени клиента строкой. Это окно — единственное место, где такой id ставится
 * на уже загруженную запись.
 *
 * Имя в гипнозио заполнено далеко не у всех (человек, вошедший через телеграм,
 * часто существует как один только ник), поэтому поиск идёт сразу по имени,
 * фамилии, имени для сертификата, почте и никам в телеграме и ВК, а в строке
 * показывается всё, по чему человека можно узнать глазами.
 */

const FILTERS = [
  { key: 'therapy', label: 'Клиенты терапии', params: { therapy: '1' } },
  { key: 'journal', label: 'С журналом сессий', params: { has_journal: '1' } },
  { key: 'telegram', label: 'Вход через телеграм', params: { via: 'telegram' } },
  { key: 'paid', label: 'С доступом к курсу', params: { min_level: '2' } },
];

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const LinkClientModal = ({ file, onClose, searchClients, updateFileAccess }) => {
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Привязка почти всегда делается ради того, чтобы человек услышал свою
  // запись, поэтому доступ открывается заодно. Галочку видно, и её можно снять.
  const [openForClient, setOpenForClient] = useState(true);
  const [copied, setCopied] = useState(false);
  const searchRef = useRef(null);

  const linkedId = file?.client_profile_id || null;
  // Ссылка, которую вставляют в «Материалы» сессии в кабинете гипнозио.
  // Открывает у клиента сразу эту запись, ниже — остальные доступные.
  const recordUrl = `${window.location.origin}/r/${file?.id}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(recordUrl);
    } catch {
      // Буфер недоступен (нет https или отказали в доступе) — выделяем текст,
      // чтобы ссылку можно было скопировать руками.
      window.prompt('Скопируйте ссылку:', recordUrl);
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    searchRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Поиск с задержкой. Пустой запрос — не пустой список: показываем последних
  // зарегистрировавшихся, чтобы окно не открывалось «в никуда».
  useEffect(() => {
    const filterParams = FILTERS.find((f) => f.key === activeFilter)?.params || {};
    setLoading(true);
    const timer = setTimeout(async () => {
      const found = await searchClients({ q: query.trim(), ...filterParams });
      setFailed(found === null);
      setResults(found || []);
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, activeFilter, searchClients]);

  const apply = useCallback(async (patch, message) => {
    setSaving(true);
    setError('');
    try {
      await updateFileAccess(file.id, patch);
      onClose(message);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Не удалось сохранить');
      setSaving(false);
    }
  }, [file, updateFileAccess, onClose]);

  // Подпись, вписанную при загрузке (`client_name`), не трогаем: имя аккаунта
  // живёт отдельным полем, иначе привязка переписывала бы то, что вы напечатали.
  const link = (client) => apply(
    {
      client_profile_id: client.id,
      client_profile_name: client.name,
      ...(openForClient ? { visibility: 'client' } : {}),
    },
    `Запись привязана к ${client.name}`,
  );

  // Отвязка закрывает запись явно: аудитория «клиенту» без клиента не значит
  // ничего, и оставлять её в таком виде — значит врать интерфейсом.
  const unlink = () => apply(
    { client_profile_id: '', client_profile_name: '', therapy_session_id: '', visibility: 'private' },
    'Привязка снята, запись закрыта',
  );

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={() => onClose()}
      data-testid="link-client-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh]"
        data-testid="link-client-modal"
      >
        <div className="flex items-center justify-between p-5 pb-3">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Link2 size={18} /> Кому принадлежит запись
          </h2>
          <button type="button" onClick={() => onClose()} className="text-gray-500 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <p className="px-5 text-xs text-gray-500 truncate">
          {file?.title}
        </p>

        {/* Текущее состояние */}
        <div className="mx-5 mt-3 rounded-lg bg-gray-900 px-3 py-2.5 flex items-center justify-between gap-3">
          {linkedId ? (
            <>
              <div className="min-w-0">
                <p className="text-sm text-green-500 truncate">
                  Привязана к {file.client_profile_name || 'выбранному человеку'}
                </p>
                <p className="text-[10px] text-gray-500">Аудитория: {file.visibility || 'private'}</p>
              </div>
              <button
                type="button"
                onClick={unlink}
                disabled={saving}
                className="flex items-center gap-1.5 text-sm text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
                data-testid="unlink-button"
              >
                <Unlink size={14} /> Отвязать
              </button>
            </>
          ) : (
            <p className="text-sm text-gray-400">
              Не привязана{file?.client_name ? ` — в поле клиента вписано «${file.client_name}»` : ''}
            </p>
          )}
        </div>

        {/* Поиск */}
        <div className="px-5 pt-4">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Имя, фамилия, почта или ник в телеграме"
              className="w-full bg-gray-900 border border-gray-700 rounded pl-9 pr-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500"
              data-testid="link-client-search"
            />
          </div>

          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setActiveFilter(activeFilter === f.key ? null : f.key)}
                className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                  activeFilter === f.key
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
                data-testid={`link-client-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Результаты */}
        <div className="flex-1 overflow-y-auto px-5 py-3 min-h-[120px]">
          {loading && results.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              <Loader2 size={18} className="inline animate-spin text-orange-400" />
            </p>
          ) : failed ? (
            <p className="text-sm text-gray-500 text-center py-6">
              Gipnozio не отвечает — попробуйте позже
            </p>
          ) : results.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              Никого не нашлось. В списке только те, у кого есть аккаунт в Gipnozio.
            </p>
          ) : (
            <div className="space-y-1">
              {results.map((c) => {
                const isLinked = c.id === linkedId;
                return (
                  <button
                    key={c.id}
                    type="button"
                    disabled={saving || isLinked}
                    onClick={() => link(c)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      isLinked ? 'bg-green-900/30' : 'hover:bg-gray-700/60'
                    } disabled:cursor-default`}
                    data-testid={`link-client-option-${c.id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-xs text-gray-300 flex-shrink-0 overflow-hidden">
                      {c.avatar_url
                        ? <img src={c.avatar_url} alt="" className="w-full h-full object-cover" />
                        : (c.name || '?').trim().charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-white truncate">{c.name}</span>
                        {c.is_therapy_client && (
                          <span className="text-[10px] text-orange-400 border border-orange-500/40 rounded px-1">терапия</span>
                        )}
                        {c.sessions_count > 0 && (
                          <span className="text-[10px] text-gray-400">журнал: {c.sessions_count}</span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500 truncate">
                        {[
                          c.telegram_username && `@${c.telegram_username}`,
                          c.vk_username && `вк: ${c.vk_username}`,
                          c.email,
                          `уровень ${c.role_level ?? 1}`,
                          c.created_at && `с ${formatDate(c.created_at)}`,
                        ].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    {isLinked && <Check size={16} className="text-green-500 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Ссылка для кабинета: скопировать и вставить в «Материалы» сессии */}
        <div className="px-5 pt-3 border-t border-gray-700">
          <div className="flex items-center gap-2 bg-gray-900 rounded-lg px-3 py-2">
            <span className="flex-1 text-xs text-gray-400 truncate font-mono">{recordUrl}</span>
            <button
              type="button"
              onClick={copyLink}
              className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-400 flex-shrink-0"
              data-testid="copy-record-link"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Скопировано' : 'Скопировать'}
            </button>
          </div>
          <p className="text-[10px] text-gray-500 mt-1">
            Ссылка для карточки сессии в кабинете. Откроется только тому, кому запись доступна.
          </p>
        </div>

        <div className="px-5 py-3">
          <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={openForClient}
              onChange={(e) => setOpenForClient(e.target.checked)}
              className="accent-orange-500"
              data-testid="link-client-open-access"
            />
            Открыть запись этому человеку (аудитория «клиенту»)
          </label>
          {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default LinkClientModal;
